// react_frontend/src/Components/Modules/HR/MyTasks.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip, Skeleton,
  LinearProgress, Stack, Avatar, Divider, TextField, MenuItem,
  Select, FormControl, InputLabel, Grid
} from "@mui/material";
import {
  FaClock, FaSpinner, FaEye, FaCheckCircle, FaFlag,
  FaCalendarAlt, FaFilter, FaSync, FaExclamationTriangle
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

const HeroCard = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(99, 102, 241, 0.25)",
}));

const TaskRow = styled(Paper)(({ theme, overdue }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  background: "white",
  border: `1px solid ${overdue ? "#ef4444" : "rgba(0,0,0,0.08)"}`,
  borderLeft: `4px solid ${overdue ? "#ef4444" : "#667eea"}`,
  marginBottom: theme.spacing(1.5),
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transform: "translateX(4px)",
  },
}));

const statusConfig = {
  todo: { label: "To Do", color: "#6b7280", icon: <FaClock />, bg: "#f3f4f6" },
  in_progress: { label: "In Progress", color: "#3b82f6", icon: <FaSpinner />, bg: "#dbeafe" },
  review: { label: "Review", color: "#f59e0b", icon: <FaEye />, bg: "#fef3c7" },
  done: { label: "Done", color: "#10b981", icon: <FaCheckCircle />, bg: "#d1fae5" },
};

const priorityConfig = {
  low: { label: "Low", color: "#10b981" },
  medium: { label: "Medium", color: "#f59e0b" },
  high: { label: "High", color: "#ef4444" },
};

const MyTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("hr/my-tasks", { params: { status: filter } });
      setTasks(res.data.data || []);
      setStats(res.data.stats || {});
    } catch (err) {
      Swal.fire("Error", "Failed to load tasks", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await AxiosAuthInstance.put(`hr/projects/tasks/${task.id}`, { status: newStatus });
      Swal.fire({ icon: "success", title: "Updated!", timer: 1200, showConfirmButton: false });
      fetchTasks();
    } catch (err) {
      Swal.fire("Error", "Failed to update", "error");
    }
  };

  const filteredTasks = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="My Tasks" />

      <HeroCard elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaCheckCircle style={{ marginRight: 10, verticalAlign: "middle" }} />
              My Tasks
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Track and manage your assigned tasks
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchTasks} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}>
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
          {[
            { label: "Total", value: stats.total || 0 },
            { label: "To Do", value: stats.todo || 0 },
            { label: "In Progress", value: stats.in_progress || 0 },
            { label: "Review", value: stats.review || 0 },
            { label: "Done", value: stats.done || 0 },
            { label: "Overdue", value: stats.overdue || 0, urgent: true },
          ].map((s, i) => (
            <Paper key={i} sx={{
              p: 1.5, bgcolor: s.urgent ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.15)",
              color: "white", borderRadius: 2, minWidth: 100, flex: 1,
            }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{s.label}</Typography>
              <Typography variant="h4" fontWeight={700}>{s.value}</Typography>
            </Paper>
          ))}
        </Box>
      </HeroCard>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FaFilter color="#667eea" />
            <Typography variant="subtitle2" fontWeight={600}>Filter:</Typography>
          </Box>
          <Chip
            label="All" onClick={() => setFilter("all")}
            sx={{ bgcolor: filter === "all" ? "#667eea" : "#f0f0f0", color: filter === "all" ? "white" : "inherit", fontWeight: 600 }}
          />
          {Object.entries(statusConfig).map(([key, cfg]) => (
            <Chip
              key={key}
              label={cfg.label}
              icon={cfg.icon}
              onClick={() => setFilter(key)}
              sx={{
                bgcolor: filter === key ? cfg.color : cfg.bg,
                color: filter === key ? "white" : cfg.color,
                fontWeight: 600,
                "& .MuiChip-icon": { color: filter === key ? "white" : cfg.color },
              }}
            />
          ))}
        </Stack>
      </Paper>

      {loading ? (
        <Stack spacing={1.5}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />)}
        </Stack>
      ) : filteredTasks.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center" }}>
          <FaCheckCircle style={{ fontSize: 64, color: "#bdbdbd", marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {filter === "all" ? "No tasks assigned to you" : `No ${filter.replace("_", " ")} tasks`}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {filteredTasks.map((task) => {
            const statusCfg = statusConfig[task.status];
            const priorityCfg = priorityConfig[task.priority];
            return (
              <TaskRow key={task.id} elevation={0} overdue={task.is_overdue}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {task.title}
                      </Typography>
                      {task.is_overdue && (
                        <Chip
                          size="small"
                          icon={<FaExclamationTriangle />}
                          label="Overdue"
                          sx={{ bgcolor: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                      <Chip
                        size="small"
                        label={task.project_name}
                        sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 600, fontSize: "0.7rem" }}
                        onClick={() => navigate(`/hr/projects/${task.project_id}`)}
                      />
                      <Chip
                        size="small"
                        icon={<FaFlag />}
                        label={priorityCfg.label}
                        sx={{ bgcolor: `${priorityCfg.color}15`, color: priorityCfg.color, fontWeight: 600, fontSize: "0.7rem" }}
                      />
                      <Chip
                        size="small"
                        icon={statusCfg.icon}
                        label={statusCfg.label}
                        sx={{ bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 600, fontSize: "0.7rem" }}
                      />
                    </Box>

                    {task.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {task.description}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {task.due_date && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaCalendarAlt size={11} color={task.is_overdue ? "#dc2626" : "#9ca3af"} />
                          <Typography variant="caption" color={task.is_overdue ? "#dc2626" : "text.secondary"}>
                            Due: {task.due_date}
                          </Typography>
                        </Box>
                      )}
                      {task.completed_at && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaCheckCircle size={11} color="#10b981" />
                          <Typography variant="caption" color="text.secondary">
                            Completed: {task.completed_at}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {task.status !== "done" && (
                      <Tooltip title="Mark as Done">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(task, "done")}
                          sx={{ bgcolor: "#d1fae5", color: "#10b981", "&:hover": { bgcolor: "#a7f3d0" } }}
                        >
                          <FaCheckCircle size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {task.status !== "in_progress" && task.status !== "done" && (
                      <Tooltip title="Start Working">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(task, "in_progress")}
                          sx={{ bgcolor: "#dbeafe", color: "#3b82f6", "&:hover": { bgcolor: "#bfdbfe" } }}
                        >
                          <FaSpinner size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </TaskRow>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default MyTasks;